import "regenerator-runtime/runtime";

// Side-effect import: initialises OpenTelemetry and patches fetch()
// before any other modules make network calls.
import "./telemetry";
import "@sentricmusic/ui/styles.css";
import Clarity from "@microsoft/clarity";
import { IconAlert, IconTick, IconValidationErrors, Toaster } from "@sentricmusic/ui";
import { QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider } from "@tanstack/react-router";
import { Analytics } from "@vercel/analytics/react";
import { SpeedInsights } from "@vercel/speed-insights/react";
import { AwsRum } from "aws-rum-web";
import { withLDProvider } from "launchdarkly-react-client-sdk";
import React, { Suspense } from "react";
import ReactDOM from "react-dom/client";
import { I18nextProvider } from "react-i18next";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "shepherd.js/dist/css/shepherd.css";

import "./index.css";
import ldDefaultUser from "./features/auth/ldDefaultUser";
import i18n from "./i18n";
import { queryClient } from "./lib/queryClient";
import { router } from "./router";

function initializeAwsRum() {
  if (import.meta.env.VITE_STAGE === "prd") {
    try {
      const config = {
        sessionSampleRate: 1,
        guestRoleArn:
          "arn:aws:iam::589126456693:role/RUM-Monitor-eu-west-1-589126456693-3425148983861-Unauth",
        identityPoolId: "eu-west-1:3517ae3d-64c2-4187-9440-a0a1844ae0c4",
        endpoint: "https://dataplane.rum.eu-west-1.amazonaws.com",
        telemetries: ["performance", "errors", "http"],
        allowCookies: true,
        enableXRay: false,
      };
      const APPLICATION_ID = "2f805d61-28e9-4fac-a724-b63ea62e633e";
      const APPLICATION_VERSION = "1.0.0";
      const APPLICATION_REGION = "eu-west-1";

      // Instantiated for side-effect only (starts RUM monitoring session)
      const _rum = new AwsRum(
        APPLICATION_ID,
        APPLICATION_VERSION,
        APPLICATION_REGION,
        config,
      );
      void _rum;
    }
    catch (error) {
      console.log(error);
      // Ignore errors thrown during CloudWatch RUM web client initialization
    }
  }
}

initializeAwsRum();

// eslint-disable-next-line ts/no-unsafe-assignment
const projectId = import.meta.env.VITE_CLARITY_PROJECT_ID;

if (import.meta.env.VITE_STAGE === "prd" || import.meta.env.VITE_STAGE === "dev") {
  // eslint-disable-next-line ts/no-unsafe-argument
  Clarity.init(projectId);

  // Send consent signal for all users (cookie policy compliance handled)
  // This must be called after Clarity.init() to enable full tracking features
  if (window.clarity) {
    window.clarity("consentv2", {
      ad_Storage: "granted",
      analytics_Storage: "granted",
    });
  }
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Suspense fallback={null}>
        <ToastContainer />
        <Toaster
          icons={{
            success: <IconTick size="md" className="stroke-black" />,
            warning: <IconAlert size="md" className="stroke-black" />,
            error: <IconValidationErrors size="md" className="stroke-black" />,
          }}
          position="top-center"
        />
        <RouterProvider router={router} />
      </Suspense>
    </QueryClientProvider>
  );
}

const LDApp = withLDProvider({
  clientSideID: import.meta.env.VITE_LAUNCH_DARKLY_ID as string,
  context: ldDefaultUser,
  reactOptions: {
    useCamelCaseFlagKeys: false,
  },
  options: {
    allAttributesPrivate: true,
  },
})(App);

const rootElement = document.getElementById("root")!;

if (!rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement);
  root.render(
    <React.StrictMode>
      <I18nextProvider i18n={i18n}>
        <LDApp />
      </I18nextProvider>
      <Suspense fallback={null}>
        <Analytics debug={false} />
      </Suspense>
      <SpeedInsights />
    </React.StrictMode>,
  );
}
